import { INCHES_PER_DECIMETER, inchesToMeters, metersToInches } from 'unit.utils';
import {
	FLAT_ROOF_DEFAULT_PITCH,
	NORMAL_FACET_DEFAULT_PITCH,
	STARTING_HEIGHT_DEFAULT_FEET,
	DEFAULT_SHADING_VAL,
	DESIGN_MODES,
} from '../../constants';

type DefaultPitchParams = {
	isGroundMount?: boolean;
	/** current design mode; regular roof facets only get a default pitch in NREL manual mode */
	mode?: DesignModes;
	/** true for SOLAR designs, where the org's configured flat pitch wins over the hardcoded flat default */
	isSolarDesign?: boolean;
	defaultFlatPitchDeg?: number;
};

/**
 * Fallback pitch for a ground mount: the org's configured flat pitch on solar designs,
 * and the flat-roof default otherwise.
 */
export function getGroundMountDefaultPitchDegrees(args: Pick<DefaultPitchParams, 'isSolarDesign' | 'defaultFlatPitchDeg'>) {
	const { isSolarDesign, defaultFlatPitchDeg } = args;
	return isSolarDesign ? (defaultFlatPitchDeg ?? FLAT_ROOF_DEFAULT_PITCH) : FLAT_ROOF_DEFAULT_PITCH;
}

/** Fallback pitch for a regular roof facet: only NREL manual mode has a default, other modes leave it null. */
export function getNormalFacetDefaultPitchDegrees(mode?: DesignModes) {
	return mode === DESIGN_MODES.NEARMAP_NREL_MANUAL ? NORMAL_FACET_DEFAULT_PITCH : null;
}

/** Single source of truth for a facet's fallback pitch, picking the ground mount or regular facet default. */
export function getFacetDefaultPitchDegrees(args: DefaultPitchParams) {
	return args.isGroundMount ? getGroundMountDefaultPitchDegrees(args) : getNormalFacetDefaultPitchDegrees(args.mode);
}

/** Resolves a facet's pitch, falling back to {@link getFacetDefaultPitchDegrees} when unset. */
export function resolveFacetPitchDegrees(
	pitchDegrees: number | null | undefined,
	params: DefaultPitchParams
) {
	return (pitchDegrees != null && !(isNaN(pitchDegrees))) ? Number(pitchDegrees) : getFacetDefaultPitchDegrees(params);
}

/** Default row spacing (meters) for a ground mount facet, derived from its resolved pitch. */
export function getFacetDefaultRowSpacingInMeters(
	pitchDegrees: number,
	groundMetersPerPixel: number,
) {
	const gapPixels = pitchDegrees / INCHES_PER_DECIMETER;
	const gapMeters = gapPixels * groundMetersPerPixel;
	const roundedInches = Math.round(metersToInches(gapMeters));
	return inchesToMeters(roundedInches);
}

/** Default starting height (feet) for a ground mount facet. */
export function getFacetDefaultStartingHeightInFeet() {
	return STARTING_HEIGHT_DEFAULT_FEET;
}

/** Default shading value for a facet, used when the org has no configured default. */
export function getFacetDefaultShading(defaultShading?: number) {
	return defaultShading ?? DEFAULT_SHADING_VAL;
}
